import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import UserContext from "context/UserContext";
import RevaText from "components/common/RevaText";
import performRequest from "network/perform-request";
import { leagueCustomerSettingsImagesRoute, leagueCustomerSettingsRoute } from "network/api-routes";
import { useGestionCustomer } from "hooks/useGestionCustomer";
import BrandingCardPalette from "./branding/BrandingCardPalette";
import BrandingColorPanel from "./branding/BrandingColorPanel";
import BrandingInteractivePreview from "./branding/BrandingInteractivePreview";
import {
  buildPatchPayload,
  mapSettingsToForm,
  pickBrandingImages,
  resolveBrandingImageUrl,
} from "./branding/branding-helpers";
import {
  cardConfigByField,
  defaultSizeForCard,
  nextCardPriority,
} from "./branding/branding-card-order";

const stickyColumnSx = {
  position: "sticky",
  top: { xs: 8, sm: 16 },
  zIndex: 2,
  alignSelf: "flex-start",
  maxHeight: { lg: "calc(100vh - 32px)" },
  overflowY: { lg: "auto" },
};

const GestionBranding = () => {
  const { customerId, customerName } = useGestionCustomer();
  const { user } = useContext(UserContext);
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingField, setUploadingField] = useState(null);
  const [selectedUploadField, setSelectedUploadField] = useState(null);
  const [form, setForm] = useState(mapSettingsToForm());
  const [logoPreview, setLogoPreview] = useState("");
  const [localPreviews, setLocalPreviews] = useState({});

  const handleError = useCallback(
    (error) => {
      enqueueSnackbar(error?.response?.data?.message || intl.formatMessage({ id: "gestion.error.generic" }), {
        variant: "error",
      });
    },
    [enqueueSnackbar, intl],
  );

  const applySettings = useCallback((data) => {
    setForm(mapSettingsToForm(data));
    setLogoPreview(resolveBrandingImageUrl(data?.image));
  }, []);

  const load = useCallback(() => {
    if (!customerId) return;
    setLoading(true);
    performRequest("GET", leagueCustomerSettingsRoute(customerId), {}, true, user)
      .then((response) => applySettings(response.data ?? {}))
      .catch(handleError)
      .finally(() => setLoading(false));
  }, [applySettings, customerId, handleError, user]);

  useEffect(() => {
    load();
  }, [load]);

  const patchForm = useCallback((updates) => {
    setForm((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleAddCard = useCallback(
    (uploadField) => {
      const card = cardConfigByField(uploadField);
      if (!card) return;
      const updates = { [card.enabledKey]: true };
      if (card.priorityKey) {
        updates[card.priorityKey] = nextCardPriority(form);
      }
      if (card.sizeKey && !form[card.sizeKey]) {
        updates[card.sizeKey] = defaultSizeForCard(card);
      }
      patchForm(updates);
      setSelectedUploadField(uploadField);
    },
    [form, patchForm],
  );

  const handleSave = () => {
    if (!customerId) return;
    setSaving(true);
    performRequest("PATCH", leagueCustomerSettingsRoute(customerId), buildPatchPayload(form), true, user)
      .then((response) => {
        enqueueSnackbar(intl.formatMessage({ id: "gestion.branding.saved" }), { variant: "success" });
        applySettings(response.data ?? {});
      })
      .catch(handleError)
      .finally(() => setSaving(false));
  };

  const handleImageUpload = (field, file) => {
    if (!customerId || !file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setLocalPreviews((prev) => ({ ...prev, [field]: reader.result }));
    };
    reader.readAsDataURL(file);

    const body = new FormData();
    body.append(field, file);
    setUploadingField(field);
    performRequest("POST", leagueCustomerSettingsImagesRoute(customerId), body, true, user)
      .then((response) => {
        enqueueSnackbar(intl.formatMessage({ id: "gestion.branding.imageUploaded" }), { variant: "success" });
        const data = response.data ?? {};
        setLogoPreview(resolveBrandingImageUrl(data.image));
        patchForm(pickBrandingImages(data));
        setLocalPreviews((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      })
      .catch(handleError)
      .finally(() => setUploadingField(null));
  };

  if (!customerId) return null;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" sx={{ py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: 1600, mx: "auto", p: { xs: 2, md: 3 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ sm: "center" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <RevaText variant="h4">
          {intl.formatMessage({ id: "gestion.branding.title" }, { name: customerName || "" })}
        </RevaText>
        <Button variant="contained" color="primary" size="small" onClick={handleSave} disabled={saving}>
          {intl.formatMessage({ id: "league.planillero.save" })}
        </Button>
      </Stack>

      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} md={3} order={{ xs: 2, md: 1 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <BrandingCardPalette
              form={form}
              selectedUploadField={selectedUploadField}
              onAdd={handleAddCard}
              onPatch={patchForm}
              onImageUpload={handleImageUpload}
              uploadingField={uploadingField}
              localPreviews={localPreviews}
            />
          </Paper>

          <Accordion sx={{ mt: 2 }} defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <RevaText variant="subtitle2">{intl.formatMessage({ id: "gestion.branding.general.title" })}</RevaText>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  size="small"
                  label={intl.formatMessage({ id: "gestion.branding.organizerName" })}
                  value={form.name}
                  onChange={(event) => patchForm({ name: event.target.value })}
                />
                <TextField
                  fullWidth
                  size="small"
                  label={intl.formatMessage({ id: "gestion.branding.facebook" })}
                  value={form.facebookUrl}
                  onChange={(event) => patchForm({ facebookUrl: event.target.value })}
                />
                <TextField
                  fullWidth
                  size="small"
                  label={intl.formatMessage({ id: "gestion.branding.instagram" })}
                  value={form.instagramUrl}
                  onChange={(event) => patchForm({ instagramUrl: event.target.value })}
                />
                <TextField
                  fullWidth
                  size="small"
                  label={intl.formatMessage({ id: "gestion.branding.twitter" })}
                  value={form.twitterUrl}
                  onChange={(event) => patchForm({ twitterUrl: event.target.value })}
                />
                <TextField
                  fullWidth
                  size="small"
                  label={intl.formatMessage({ id: "gestion.branding.whatsapp" })}
                  value={form.whatsapp}
                  onChange={(event) => patchForm({ whatsapp: event.target.value })}
                />
                <TextField
                  fullWidth
                  size="small"
                  label={intl.formatMessage({ id: "gestion.players.email" })}
                  value={form.email}
                  onChange={(event) => patchForm({ email: event.target.value })}
                />
                <Box>
                  {logoPreview && (
                    <Box
                      component="img"
                      src={logoPreview}
                      alt=""
                      sx={{ maxHeight: 64, maxWidth: 160, mb: 1, display: "block" }}
                    />
                  )}
                  <Button variant="outlined" size="small" component="label" disabled={uploadingField === "logo"}>
                    {intl.formatMessage({ id: "gestion.branding.uploadLogo" })}
                    <input
                      hidden
                      type="file"
                      accept="image/jpeg,image/png,image/jpg"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (file) handleImageUpload("logo", file);
                        event.target.value = "";
                      }}
                    />
                  </Button>
                </Box>
                <RevaText variant="caption" color="text.secondary">
                  {intl.formatMessage({ id: "gestion.branding.ads.controlHelp" })}
                </RevaText>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  {["ad1Available", "ad2Available", "ad3Available"].map((key, index) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Switch
                          checked={Boolean(form[key])}
                          onChange={(event) => patchForm({ [key]: event.target.checked })}
                        />
                      }
                      label={intl.formatMessage({ id: "gestion.branding.ads.control" }, { n: index + 1 })}
                    />
                  ))}
                </Stack>
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
          <Box sx={stickyColumnSx}>
            <BrandingInteractivePreview
              form={form}
              localPreviews={localPreviews}
              logoPreview={logoPreview}
              selectedUploadField={selectedUploadField}
              onSelect={setSelectedUploadField}
              onPatch={patchForm}
              onDropCard={handleAddCard}
              onReorder={patchForm}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={3} order={{ xs: 3, md: 3 }}>
          <Box sx={stickyColumnSx}>
            <BrandingColorPanel form={form} onPatch={patchForm} />
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" color="primary" size="small" onClick={handleSave} disabled={saving}>
          {intl.formatMessage({ id: "league.planillero.save" })}
        </Button>
      </Box>
    </Box>
  );
};

export default GestionBranding;
